import {
  ConsultantSummary,
  DistributionGroup,
  RequiredTagsMode,
  RuleWeight,
  TagCondition,
} from "@features/lead-routing/leadRouting.types"

// Case-insensitive: does the consultant satisfy one tag condition for the given
// mode? "any" = carries any of the tags, "all" = carries every one, "none" =
// carries none of them. An empty tag set matches everyone. Mirrors the backend
// matchesRequiredTags.
export function consultantHasRequiredTags(
  tags: string[],
  requiredTags: string[],
  mode: RequiredTagsMode = "any",
): boolean {
  if (requiredTags.length === 0) {
    return true
  }
  const owned = new Set(tags.map((tag) => tag.toLowerCase()))
  const wanted = requiredTags.map((tag) => tag.toLowerCase())
  if (mode === "all") {
    return wanted.every((tag) => owned.has(tag))
  }
  if (mode === "none") {
    return !wanted.some((tag) => owned.has(tag))
  }
  return wanted.some((tag) => owned.has(tag))
}

// A rule can stack several tag conditions, all AND'd. Mirrors the backend
// matchesTagConditions; an empty list matches everyone.
export function consultantMatchesTagConditions(
  tags: string[],
  conditions: TagCondition[],
): boolean {
  return conditions.every((condition) =>
    consultantHasRequiredTags(tags, condition.tags, condition.mode),
  )
}

// Whether a rule has an actual tag gate (a condition carrying tags).
export function hasTagGate(conditions: TagCondition[]): boolean {
  return conditions.some((condition) => condition.tags.length > 0)
}

// Even split by default; a stored weight overrides it for a consultant (0
// excludes). A member with no stored weight falls in at the average of the
// listed positive weights, so an all-even rule stays even as the group grows.
// Mirrors the backend tagMemberWeights. `keepExcluded` retains weight-0
// members (for the editor, which must render and persist exclusions);
// routing/live-split displays drop them.
function tagMemberWeights(
  memberIds: string[],
  storedWeights: RuleWeight[],
  keepExcluded = false,
): RuleWeight[] {
  const stored = new Map(storedWeights.map((w) => [w.consultantId, w.weight]))
  const listed = memberIds
    .map((id) => stored.get(id))
    .filter((weight): weight is number => weight != null && weight > 0)
  const evenDefault = listed.length
    ? listed.reduce((sum, weight) => sum + weight, 0) / listed.length
    : 1
  const resolved = memberIds.map((id) => ({
    consultantId: id,
    weight: stored.get(id) ?? evenDefault,
  }))
  return keepExcluded ? resolved : resolved.filter((c) => c.weight > 0)
}

// Whether a rule splits by group (a slice gating on a tag or naming a person).
export function hasDistributionGroups(
  groups: DistributionGroup[] | undefined,
): boolean {
  return (
    !!groups &&
    groups.some(
      (group) =>
        hasTagGate(group.conditions ?? []) ||
        (group.consultantIds?.length ?? 0) > 0,
    )
  )
}

// Compile a tag-grouped distribution to per-consultant weights: a consultant
// belongs to EVERY group that targets them (carrying its tags or named by id),
// and their weight is the sum of their share of each — a group's weight split
// evenly among that group's members. Overlap is deliberate (nested tiers);
// group order carries no meaning. Mirrors the backend groupedMemberWeights.
// Anyone in no group isn't routed by this rule.
export function groupedMemberWeights(
  groups: DistributionGroup[],
  consultants: ConsultantSummary[],
): RuleWeight[] {
  const groupConsultantSets = groups.map(
    (group) => new Set(group.consultantIds ?? []),
  )
  const memberships = consultants.map((consultant) =>
    groups.map(
      (group, i) =>
        groupConsultantSets[i].has(consultant.id) ||
        (hasTagGate(group.conditions ?? []) &&
          consultantMatchesTagConditions(consultant.tags, group.conditions)),
    ),
  )
  const counts = groups.map((_, i) =>
    memberships.reduce((count, inGroups) => count + (inGroups[i] ? 1 : 0), 0),
  )
  const weights: RuleWeight[] = []
  consultants.forEach((consultant, m) => {
    let total = 0
    memberships[m].forEach((inGroup, i) => {
      if (inGroup && counts[i] > 0) {
        total += groups[i].weight / counts[i]
      }
    })
    if (total > 0) {
      weights.push({ consultantId: consultant.id, weight: total })
    }
  })
  return weights
}

// The live membership + split of a rule for display: a tag-gated rule routes to
// whoever currently carries its required tags (dynamic), so its split is
// resolved from the current consultants rather than the saved snapshot. A
// non-tag rule routes to exactly its stored weights. `keepExcluded` keeps
// deliberately-excluded (weight 0) members so the editor can show/persist them.
export function resolveRuleSplit(
  rule: {
    weights: RuleWeight[]
    requiredTagConditions?: TagCondition[]
    distributionGroups?: DistributionGroup[]
  },
  consultants: ConsultantSummary[],
  keepExcluded = false,
): RuleWeight[] {
  // A grouped rule's split is its groups compiled over the current consultants.
  if (hasDistributionGroups(rule.distributionGroups)) {
    return groupedMemberWeights(rule.distributionGroups ?? [], consultants)
  }
  const conditions = rule.requiredTagConditions ?? []
  if (!hasTagGate(conditions)) {
    return rule.weights
  }
  const storedById = new Map(
    rule.weights.map((w) => [w.consultantId, w.weight]),
  )
  // Members = whoever satisfies the tag gate PLUS anyone added by hand (a
  // positive stored weight), even without matching. In the editor
  // (keepExcluded) a deactivated manual add (weight 0) is also kept so it stays
  // visible and re-includable.
  const memberIds = consultants
    .filter((consultant) => {
      if (consultantMatchesTagConditions(consultant.tags, conditions)) {
        return true
      }
      const stored = storedById.get(consultant.id)
      if (stored == null) {
        return false
      }
      return stored > 0 || keepExcluded
    })
    .map((consultant) => consultant.id)
  return tagMemberWeights(memberIds, rule.weights, keepExcluded)
}
